import clsx from 'clsx';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import useEmblaCarousel from 'embla-carousel-react';
import Lottie from 'lottie-web';
import {useEffect, useRef} from 'react';
import {Image} from '@shopify/hydrogen';

import type {SanityModuleImageGrid} from '../../types';

import ModuleWrapper from './ModuleWrapper.client';

import {isMobile} from '../ui/animationVars';
import {gridVars} from '../ui/animationVars';
import {contentVars} from '../ui/animationVars';

const LottiePlayer = ({lottie, inView}: any) => {
  const ref = useRef<any>();
  useEffect(() => {
    const anim = Lottie.loadAnimation({
      container: ref.current, // the dom element that will contain the animation
      renderer: 'svg',
      loop: false,
      autoplay: inView,
      path: lottie?.asset?.url, // the path to the animation json
    });
    anim.play();

    return () => {
      anim.destroy();
    };
  }, [inView]);

  return (
    <motion.div variants={contentVars} className="h-full w-full">
      <div ref={ref} className="h-full w-auto" />
    </motion.div>
  );
};

const GridMobile = ({items, inView, numerate}: any) => {
  /* Carousel & button handling */
  const [viewportRef] = useEmblaCarousel({
    align: 'start',
    dragFree: true,
    loop: false,
    containScroll: 'keepSnaps',
  });

  return (
    <div ref={viewportRef} className="w-full">
      <div className="flex gap-x-10 lg:hidden">
        {items?.map(({image, heading, copy, externalLink}: any, i: number) => (
          <a
            href={externalLink || '/'}
            rel="noreferrer"
            target="_blank"
            key={i}
            style={{flex: '0 0 auto'}}
            className={clsx([
              'flex w-1/2 flex-col gap-y-3 lg:w-2/3',
              !heading && !copy
                ? 'items-center justify-center'
                : 'justify-start',
              !externalLink && 'pointer-events-none',
            ])}
          >
            <motion.div
              initial={{opacity: 0}}
              animate={inView ? {opacity: 1} : {opacity: 0}}
              className="relative h-[125px] w-full lg:h-[225px]"
            >
              {image && (
                <Image
                  width="inherit"
                  height="inherit"
                  alt={heading || copy || 'image'}
                  data={image}
                  loading="lazy"
                  className="absolute top-0 left-0 h-full w-full object-contain"
                />
              )}
            </motion.div>
            {numerate && (
              <div className="blockH9 flex h-10 w-10 items-center justify-center rounded-full border-2 font-bold">
                {i + 1}{' '}
              </div>
            )}
            {heading && (
              <p className="blockH6 font-bold text-orange">{heading}</p>
            )}
            {copy && (
              <p className={clsx(['blockH9', !heading && 'font-bold'])}>
                {copy}
              </p>
            )}
          </a>
        ))}
      </div>
    </div>
  );
};

const GridDesktop = ({items, inView, numerate}: any) => {
  return (
    <div
      className={clsx([
        'hidden grid-cols-2 justify-center gap-x-6 gap-y-10 lg:grid lg:gap-x-12 xl:flex-nowrap',
        items.length % 3 === 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-4',
      ])}
    >
      {items?.map(
        (
          {image, heading, copy, lottie, isLottie, externalLink}: any,
          i: number,
        ) => (
          <a
            key={i}
            href={externalLink || '/'}
            target="_blank"
            rel="noreferrer"
            className={clsx([!externalLink && 'pointer-events-none'])}
          >
            <div
              className={clsx([
                'flex w-full flex-col gap-y-3',
                !heading && !copy && 'items-center justify-center',
              ])}
            >
              <div
                className={clsx([
                  'flex items-center justify-center',
                  isLottie ? 'h-[250px]' : 'h-[165px]',
                ])}
              >
                {isLottie ? (
                  <LottiePlayer inView={inView} lottie={lottie} />
                ) : (
                  image?.url && (
                    <motion.img
                      variants={contentVars}
                      src={image.url}
                      alt={heading}
                      loading="lazy"
                      className="mx-auto w-3/4 md:w-1/2 lg:max-h-[165px] lg:w-auto"
                    />
                  )
                )}
              </div>
              {numerate && (
                <motion.div
                  variants={contentVars}
                  className="blockH9 flex h-10 w-10 items-center justify-center rounded-full border-2 font-bold"
                >
                  {i + 1}{' '}
                </motion.div>
              )}
              {heading && (
                <motion.p
                  variants={contentVars}
                  className="blockH6 font-bold text-orange"
                >
                  {heading}
                </motion.p>
              )}
              {copy && (
                <motion.p
                  variants={contentVars}
                  className={clsx(['blockH9', !heading && 'font-bold'])}
                >
                  {copy}
                </motion.p>
              )}
            </div>
          </a>
        ),
      )}
    </div>
  );
};

export default function ImageGridModule({
  module,
}: {
  module: SanityModuleImageGrid;
}) {
  const {config, heading, items, numerate} = module;

  const {ref, inView} = useInView({
    threshold: 0.7,
    triggerOnce: true,
  });

  return (
    <ModuleWrapper
      {...config}
      className={clsx([
        'overflow-hidden px-gutter',
        numerate ? 'text-left' : 'text-center',
      ])}
    >
      <motion.div
        ref={ref}
        variants={gridVars}
        animate={!isMobile && (inView ? 'animate' : 'initial')}
        data-testid="section-image-grid"
      >
        {heading && (
          <motion.h1
            variants={contentVars}
            className="richH1 mb-7 font-bold md:mb-20"
          >
            {heading}
          </motion.h1>
        )}
        <GridDesktop items={items} inView={inView} numerate={numerate} />
        <GridMobile items={items} inView={inView} numerate={numerate} />
      </motion.div>
    </ModuleWrapper>
  );
}
